/**
 * Invited Welcome Step Component
 *
 * Step 2: Success confirmation and guidance for invited users.
 * Capabilities shown are personalized based on the user's role.
 *
 * @module components/onboarding/InvitedWelcomeStep
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks';
import { Button } from '@/components/ui';
import { Key, BarChart3, Users, LayoutDashboard, Eye } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

export interface InvitedWelcomeStepProps {
  /** Organization name the user joined */
  organizationName: string;
  /** Callback to complete onboarding */
  onComplete: () => Promise<void>;
}

interface Capability {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// =============================================================================
// Component
// =============================================================================

export function InvitedWelcomeStep({
  organizationName,
  onComplete,
}: InvitedWelcomeStepProps) {
  const navigate = useNavigate();
  const { canManageApiKeys } = usePermissions();

  // Personalize capabilities based on user's role/permissions
  const capabilities = useMemo((): Capability[] => {
    // First capability varies based on API key management permission
    const apiKeyCapability: Capability = canManageApiKeys
      ? {
          icon: <Key className="h-5 w-5" />,
          title: 'Manage API keys',
          description: 'Create, view, and revoke API keys for your team.',
        }
      : {
          icon: <Eye className="h-5 w-5" />,
          title: 'View API key activity',
          description: 'See key labels, creation dates, and usage history.',
        };

    return [
      apiKeyCapability,
      {
        icon: <BarChart3 className="h-5 w-5" />,
        title: 'Monitor API usage',
        description: 'Track requests, errors, and performance metrics.',
      },
      {
        icon: <Users className="h-5 w-5" />,
        title: 'Collaborate with your team',
        description: 'Work together on API projects and share insights.',
      },
    ];
  }, [canManageApiKeys]);

  const handleGoToDashboard = async () => {
    await onComplete();
    navigate('/overview');
  };

  return (
    <div className="space-y-8">
      {/* Success Message */}
      <div className="text-center">
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          🎉 Congratulations!
        </h2>
        <p className="text-gray-500 mt-2">
          You've successfully joined <strong>{organizationName}</strong>.
        </p>
      </div>

      {/* Capabilities */}
      <div className="space-y-3">
        {capabilities.map((item) => (
          <div
            key={item.title}
            className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg"
          >
            <div className="flex-shrink-0 p-2 bg-white text-brand-600 rounded-lg shadow-sm">
              {item.icon}
            </div>
            <div>
              <p className="font-medium text-gray-900">{item.title}</p>
              <p className="text-sm text-gray-500">{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <Button
        fullWidth
        size="lg"
        onClick={handleGoToDashboard}
        rightIcon={<LayoutDashboard className="h-5 w-5" />}
      >
        Go to Dashboard
      </Button>
    </div>
  );
}
